import { AbstractCommand } from './AbstractCommand';
import { Element } from '../models/Element';
import { Video } from '@src/types.ts/story';
import { LayoutElementsResult } from '@src/utility/stackedLayout';

export abstract class AbstractLayoutCommand extends AbstractCommand {
  protected canUndo: boolean = false;
  protected extraElementDataSnapshot: Video['extraElementData'];

  constructor(editor: any, element: Element) {
    super(editor, element);
    this.extraElementDataSnapshot = this.extraElementData.cloneValue();
  }

  undo(): boolean {
    if (!this.canUndo) {
      return false;
    }

    this.extraElementData.restoreValue(this.extraElementDataSnapshot);

    return super.undo();
  }

  protected pickAllLayoutElements(element: Element): {
    parent: Element;
    children: Element[];
    data: LayoutElementsResult;
  } | null {
    const layoutElementsData =
      this.extraElementData.findLayoutRelatedElements(element);
    if (
      !layoutElementsData.isHiddenParent &&
      !layoutElementsData.isLayoutChild
    ) {
      return null;
    }

    // Get parent and children elements directly from layoutElementsData
    const parentElement = layoutElementsData.hiddenParentId
      ? this.editor.timeline.getElement(layoutElementsData.hiddenParentId)
      : null;
    const childrenElements = layoutElementsData.layoutChildrenIds
      .map((id: string) => (id ? this.editor.timeline.getElement(id) : null))
      .filter((e): e is Element => e !== null);

    if (!parentElement || childrenElements.length !== 2) {
      return null;
    }

    return {
      parent: parentElement,
      children: childrenElements,
      data: layoutElementsData,
    };
  }

  /**
   * Gets the top and bottom elements from an array of layout children elements
   * @param childrenElements Array of elements that are children in a layout
   * @returns Object containing top and bottom elements, or null if not found
   */
  protected separateChildrenByPosition(
    childrenElements: Element[],
  ): { top: Element; bottom: Element } | null {
    const topElement = childrenElements.find((e) => {
      const eed = this.extraElementData.getElementData(e);
      return eed?.layoutPosition === 'top';
    });

    const bottomElement = childrenElements.find((e) => {
      const eed = this.extraElementData.getElementData(e);
      return eed?.layoutPosition === 'bottom';
    });

    if (!topElement || !bottomElement) {
      return null;
    }

    return { top: topElement, bottom: bottomElement };
  }
}
